import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

/**
 * Native App Store update detection.
 *
 * iOS doesn't push a notification when a new App Store version drops —
 * the app has to ask Apple's iTunes Search API itself. This helper does
 * exactly that and compares the returned version against the running
 * native version. If the App Store has a newer version, the in-app
 * footer can surface a pill that opens the App Store directly to the
 * TrybeUP listing.
 *
 * Capgo OTA bundles use a separate channel and are detected by
 * useVersionCheck — this helper is only for full App Store releases
 * (a new IPA that requires Apple review).
 *
 * App Store ID `6766032580` is TrybeUP's iOS App ID, also wired into
 * `apps/web/index.html` as the apple-itunes-app smart banner ID.
 */
const APP_STORE_ID = "6766032580";
const BUNDLE_ID = "com.trybeup.app";

/** Compare two dotted version strings ("1.1.3" > "1.1.2"). */
function isNewerVersion(latest: string, current: string): boolean {
  const a = latest.split(".").map((n) => parseInt(n, 10) || 0);
  const b = current.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const ai = a[i] ?? 0;
    const bi = b[i] ?? 0;
    if (ai > bi) return true;
    if (ai < bi) return false;
  }
  return false;
}

export interface AppStoreUpdateState {
  /** True only when we have confirmed a strictly-newer version on the
   *  App Store than what's installed. False during checking, on web, or
   *  when the running version matches the store. */
  hasUpdate: boolean;
  /** Version string on the App Store, when known. */
  storeVersion: string | null;
  /** Version string currently running (info.version from @capacitor/app). */
  currentVersion: string | null;
}

let cached: AppStoreUpdateState = {
  hasUpdate: false,
  storeVersion: null,
  currentVersion: null,
};
let inFlight: Promise<AppStoreUpdateState> | null = null;

/**
 * Run the App Store check and cache the result for the session.
 * Web is a no-op (the App Store isn't relevant in a browser tab).
 *
 * Pass `{ force: true }` to bypass the session cache — used by
 * VersionUpdateBanner on background→foreground transitions so a user
 * who opened the app before an Apple release picks up the banner the
 * moment they return, without needing a force-quit. The cache otherwise
 * holds for the rest of the session.
 */
export async function checkAppStoreVersion(
  opts: { force?: boolean } = {},
): Promise<AppStoreUpdateState> {
  // iOS ONLY: this checks the Apple App Store and deep-links to itms-apps://.
  // Running it on Android compared Apple's version to the Android build and
  // could pin an undismissable "update" banner with a dead link.
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return cached;
  if (inFlight) return inFlight;
  if (cached.storeVersion && !opts.force) return cached;

  inFlight = (async () => {
    try {
      const info = await CapApp.getInfo();
      const currentVersion = info.version;
      // iTunes Search API is CORS-friendly from WKWebView. The bundleId
      // query is more reliable than ID lookup — same app, fewer
      // ambiguous results across regions / country storefronts.
      const res = await fetch(
        `https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(BUNDLE_ID)}`,
        { cache: "no-store" },
      );
      if (!res.ok) return cached;
      const data = (await res.json()) as { results?: Array<{ version?: string }> };
      const storeVersion = data.results?.[0]?.version ?? null;
      if (!storeVersion) return cached;
      cached = {
        hasUpdate: isNewerVersion(storeVersion, currentVersion),
        storeVersion,
        currentVersion,
      };
      return cached;
    } catch {
      // Network blip, ad-blocker, or a region without iTunes — best
      // effort. We just don't surface the pill in those cases.
      return cached;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function getAppStoreUpdateState(): AppStoreUpdateState {
  return cached;
}

/**
 * Deep-link the user straight to the TrybeUP App Store listing. The
 * `itms-apps://` scheme opens the App Store app directly to the page
 * (instead of going through Safari first). Falls back to the regular
 * https URL if `itms-apps://` is unavailable (some configurations).
 */
export function openAppStoreListing(): void {
  const itms = `itms-apps://apps.apple.com/app/id${APP_STORE_ID}`;
  const https = `https://apps.apple.com/app/id${APP_STORE_ID}`;
  try {
    window.location.href = itms;
    // If itms-apps:// doesn't resolve (rare on iOS), fall back to https
    // after a short delay. iOS will silently ignore the second navigation
    // if the first succeeded.
    setTimeout(() => { try { window.location.href = https; } catch {} }, 400);
  } catch {
    try { window.location.href = https; } catch {}
  }
}
